/**
 * Frontend permission matching — mirrors the backend
 * (models/Permission.js: permissionIncludes + getPermissionHierarchy) so the UI
 * grants/denies exactly what the API does.
 *
 * Why this exists: registry permissions are scoped 3-segment keys
 * (e.g. "amazon:read:own"), but UI gates check the flat level form
 * (e.g. "amazon:read"). Matching on the LEVEL segment (2nd part) — not the
 * scope — lets a user granted "amazon:read:own" satisfy an "amazon:read" gate,
 * just like the backend does. Higher levels also satisfy lower ones.
 */

// Keep in sync with models/Permission.js getPermissionHierarchy()
export const PERMISSION_HIERARCHY = {
  read: 1,
  write: 2, share: 2, cards: 2, control: 2, operate: 2,
  team: 3, assign: 3, staff: 3, phonebook: 3, issues: 3, requests: 3, users: 3,
  doors: 3, hvac: 3, electrical: 3, lighting: 3, security: 3, maintenance: 3, manage: 3,
  playlists: 3, scenes: 3, effects: 3, presets: 3, routing: 3, configure: 3, record: 3,
  dsp: 3, zones: 3, weather: 3, override: 3,
  global: 4, all: 4, others: 4, category: 4, categories: 4, responses: 4, reconcile: 4,
  bank: 4, sync: 4, levels: 4, schedules: 4, schedule: 4, holidays: 4, policies: 4,
  alerts: 4, reports: 4, import: 4, export: 4,
  approve: 5, settings: 5, delete: 6, admin: 7, emergency: 8,

  // Action levels used by scoped permissions across the catalog that had no
  // rank here — see models/Permission.js getPermissionHierarchy() for why.
  apps: 6, archive: 6, bandwidth: 6, campaigns: 6, clients: 6, close: 6,
  create: 6, dashboard: 6, deprovision: 6, devices: 6, discover: 6, edit: 6,
  guest: 6, inspect: 6, link: 6, members: 6, metrics: 6, mibs: 6, modify: 6,
  monitor: 6, publish: 6, reclaim: 6, recurring: 6, reminders: 6, request: 6,
  review: 6, revoke: 6, run: 6, send: 6, service: 6, stats: 6, templates: 6,
  upload: 6, use: 6, verify: 6,
};

/**
 * The level vocabulary: the second segment of every flat two-segment key in
 * config/comprehensivePermissions.js. Written out literally rather than derived
 * at runtime because the browser cannot require a server config.
 * Keep in sync with models/Permission.js PERMISSION_LEVELS.
 */
export const PERMISSION_LEVELS = new Set([
  'admin', 'alerts', 'all', 'approve', 'archive', 'assign', 'bandwidth', 'bank',
  'cards', 'categories', 'category', 'clients', 'close', 'configure', 'control',
  'create', 'dashboard', 'delete', 'deprovision', 'discover', 'dsp', 'effects',
  'emergency', 'export', 'global', 'guest', 'import', 'inspect', 'link',
  'maintenance', 'manage', 'metrics', 'mibs', 'modify', 'monitor', 'operate',
  'others', 'override', 'playlists', 'presets', 'read', 'reclaim', 'reconcile',
  'record', 'recurring', 'reminders', 'reports', 'request', 'review', 'revoke',
  'routing', 'run', 'scenes', 'schedule', 'service', 'share', 'stats', 'sync',
  'team', 'templates', 'upload', 'use', 'verify', 'weather', 'write', 'zones',
]);

/**
 * Scope breadth. A broader scope satisfies a narrower one ("read any user"
 * covers "read your own user"), never the reverse. Scopes outside this ladder
 * — sub-area names like "doors", and "others", which plausibly means "other
 * people's, excluding mine" — must match exactly.
 * Keep in sync with models/Permission.js SCOPE_BREADTH.
 */
export const SCOPE_BREADTH = {
  own: 1, self: 1,
  team: 2, category: 2,
  all: 3, any: 3, global: 3,
};

/**
 * Split a permission key into { entity, scope, level }.
 *
 * Three-segment keys exist in BOTH orders: entity:level:scope
 * ("amazon:read:own") and entity:scope:level ("accessControl:doors:read").
 * Only reorder when it is unambiguous — segment 3 is a known level and
 * segment 2 is not. Ambiguous families ("shortcuts:read:global", where both
 * segments are levels) and unknown ones ("news:publish:own", where neither is)
 * keep the historical entity:level:scope reading, so their behaviour does not
 * move. Keys with more than three segments likewise keep the old reading.
 */
export const parsePermission = (key) => {
  const parts = key.split(':');
  if (parts.length === 3 && PERMISSION_LEVELS.has(parts[2]) && !PERMISSION_LEVELS.has(parts[1])) {
    return { entity: parts[0], scope: parts[1], level: parts[2] };
  }
  return { entity: parts[0], scope: parts[2] ?? null, level: parts[1] };
};

/**
 * Does a granted permission satisfy a required permission?
 * @param {string} granted  a permission the user holds (e.g. "amazon:read:own", "amazon:admin", "*")
 * @param {string} required the permission being checked (e.g. "amazon:read")
 * @returns {boolean}
 */
export const permissionIncludes = (granted, required) => {
  if (!granted || !required) return false;
  if (granted === '*') return true;
  if (granted === required) return true;

  if (!granted.includes(':') || !required.includes(':')) return false;

  const g = parsePermission(granted);
  const r = parsePermission(required);

  if (g.entity !== r.entity) return false;

  // Entity wildcard, e.g. "accessControl:*"
  if (g.level === '*') return true;

  // A grant in one scope never satisfies a requirement in another
  // ("accessControl:doors:read" must not reach "accessControl:users:write"),
  // unless the granted scope is strictly broader on the breadth ladder.
  // A scoped grant may still satisfy an unscoped gate — "amazon:read:own"
  // satisfying "amazon:read" is intended — so only compare when both are scoped.
  if (g.scope && r.scope && g.scope !== r.scope) {
    const gb = SCOPE_BREADTH[g.scope];
    const rb = SCOPE_BREADTH[r.scope];
    if (gb === undefined || rb === undefined) return false;
    if (gb < rb) return false;
  }

  const h = PERMISSION_HIERARCHY;
  return h[g.level] !== undefined && h[r.level] !== undefined && h[g.level] >= h[r.level];
};

/**
 * Does any permission in a granted list satisfy the required permission?
 */
export const anyGrantSatisfies = (grantedList, required) =>
  Array.isArray(grantedList) && grantedList.some((g) => permissionIncludes(g, required));
